import React, { useState, useEffect, useCallback } from 'react';
import { SearchIcon, FilterIcon, XIcon, Loader2Icon } from 'lucide-react';
import { VISA_TYPES } from '../../types/knowledge-base';

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
  filters: {
    visaTypes: string[];
    contentType?: string;
    tags: string[];
  };
  onFiltersChange: (filters: any) => void;
  isSearching?: boolean;
  resultCount?: number;
}

export const SearchBar: React.FC<SearchBarProps> = ({
  value,
  onChange,
  filters,
  onFiltersChange,
  isSearching = false,
  resultCount,
}) => {
  const [localValue, setLocalValue] = useState(value);

  // Sync local value with external value
  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      if (localValue !== value) {
        onChange(localValue);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [localValue, onChange, value]);

  const handleVisaTypeToggle = (visaType: string) => {
    const newVisaTypes = filters.visaTypes.includes(visaType)
      ? filters.visaTypes.filter((v) => v !== visaType)
      : [...filters.visaTypes, visaType];
    onFiltersChange({ ...filters, visaTypes: newVisaTypes });
  };

  const clearSearch = useCallback(() => {
    setLocalValue('');
    onChange('');
  }, [onChange]);

  const clearFilters = useCallback(() => {
    onFiltersChange({ visaTypes: [], tags: [] });
  }, [onFiltersChange]);

  const hasActiveFilters = filters.visaTypes.length > 0 || filters.tags.length > 0;
  const hasSearchQuery = localValue.length > 0;

  return (
    <div className="space-y-3">
      {/* Search Input */}
      <div className="relative">
        {isSearching ? (
          <Loader2Icon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-blue-500 animate-spin" />
        ) : (
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
        )}
        <input
          type="text"
          value={localValue}
          onChange={(e) => setLocalValue(e.target.value)}
          placeholder="Search by title, content, or tags..."
          className="w-full pl-10 pr-20 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
        />
        {hasSearchQuery && (
          <button
            onClick={clearSearch}
            className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100"
            title="Clear search"
          >
            <XIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Search Results Count */}
      {hasSearchQuery && resultCount !== undefined && (
        <div className="text-sm text-gray-500">
          {resultCount === 0 ? (
            <span className="text-amber-600">No documents found matching "{localValue}"</span>
          ) : (
            <span>
              Found <span className="font-medium text-gray-700">{resultCount}</span> document{resultCount !== 1 ? 's' : ''} matching "<span className="font-medium text-blue-600">{localValue}</span>"
            </span>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <FilterIcon className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-600 font-medium">Visa Types:</span>
        </div>
        {VISA_TYPES.map((visaType) => (
          <button
            key={visaType}
            onClick={() => handleVisaTypeToggle(visaType)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
              filters.visaTypes.includes(visaType)
                ? 'bg-blue-600 text-white shadow-sm'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {visaType}
          </button>
        ))}
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="px-2 py-1 rounded-full text-xs font-medium text-red-600 hover:bg-red-50 transition-all flex items-center gap-1"
          >
            <XIcon className="w-3 h-3" />
            Clear filters
          </button>
        )}
      </div>
    </div>
  );
};
